import {HttpClient, HttpErrorResponse, HttpHeaders, HttpResponse} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {KeycloakService} from 'keycloak-angular';
import {catchError, EMPTY, Observable} from 'rxjs';
import {fromPromise} from 'rxjs/internal/observable/innerFrom';
import {switchMap, tap} from 'rxjs/operators';

import {environment} from '../../../../environments/environment';
import {UsersFilter} from '../../models/users/users.filter';
import {PopulatedUser, User, UserDTO} from '../../models/users/users.model';
import {optimizeFilter} from '../../utils/filter.helper';

@Injectable({
  providedIn: 'root'
})
export class UsersService {
  private usersUrl = environment.baseURL + environment.users;
  private adminUrl = environment.baseURL + environment.admin;

  constructor(
    private http: HttpClient,
    private keycloakService: KeycloakService,
    private router: Router,
  ) {
  }

  createOne<K extends keyof User>(user: User, populate?: K[]): Observable<PopulatedUser<K>> {
    return this.http.post<PopulatedUser<K>>(this.usersUrl, user, populate && {params: {populate}});
  }

  getAll<K extends keyof User>(query: UsersFilter & { populate: (K | `${K}:${string}`)[] }): Observable<{ docs: PopulatedUser<K>[], total: number }> {
    return this.http.get<{ docs: PopulatedUser<K>[], total: number }>(this.usersUrl, {params: optimizeFilter(query)});
  }

  getCurrentUser<K extends keyof User>(populate: K[], registerRedirect = true): Observable<PopulatedUser<K>> {
    return this.http.get<PopulatedUser<K>>(`${this.usersUrl}/current`, {params: {populate}}).pipe(
      catchError(err => {
        if (err instanceof HttpErrorResponse && err.status === 404) {
          return fromPromise(this.keycloakService.loadUserProfile()).pipe(
            catchError(() => {
              this.keycloakService.login();
              return EMPTY;
            }),
            switchMap(profile => this.createOne({
              _id: '',
              kcId: profile.id || '',
              email: profile.email || '',
              firstname: profile.firstName || '',
              lastname: profile.lastName || '',
              abonnement: {
                email: profile.email || '',
                topics: [],
                subjectAreas: [],
                days: [],
              },
            }, populate)),
          );
        } else if (err instanceof HttpErrorResponse && err.status === 401) {
          this.keycloakService.login();
          return EMPTY;
        } else {
          throw err;
        }
      }),
      tap(user => {
        // @ts-ignore
        if (registerRedirect && (!user.institution || !user.abonnement)) {
          this.router.navigate(['/register']);
        }
      })
    );
  }

  getOne<K extends keyof User>(id: string, populate: K[] = []): Observable<PopulatedUser<K>> {
    return this.http.get<PopulatedUser<K>>(`${this.usersUrl}/${id}`, {params: {populate}});
  }

  updateOne<K extends keyof User>(id: string, dto: UserDTO, populate: K[] = []): Observable<PopulatedUser<K>> {
    return this.http.put<PopulatedUser<K>>(`${this.usersUrl}/${id}`, dto, {params: {populate}});
  }

  updatePassword(): Observable<User> {
    return this.http.patch<User>(`${this.adminUrl}/users/password`, {});
  }

  deleteOne(id: string): Observable<User> {
    return this.http.delete<User>(`${this.usersUrl}/${id}`);
  }

  exportUsers(params: any): Observable<HttpResponse<ArrayBuffer>> {
    return this.http.get(`${this.usersUrl}/export`, {
      headers: new HttpHeaders({
        charset: 'UTF-8',
      }),
      params,
      observe: 'response',
      responseType: 'arraybuffer',
    });
  }
}
